import React, { useEffect, useMemo, useState } from "react";
import poolinsLogo from "../../assets/img/poolins.jpg";
import "../../assets/styles/mobileAppPages.css";
import {
  downloadUrlForRelease,
  formatDate,
  formatFileSize,
  releaseIsEmpty,
} from "./mobileAppUtils";

const IconDoc = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h5" />
  </svg>
);

const IconAlert = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconClock = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const IconCard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="7" y1="15" x2="9" y2="15" />
    <line x1="11" y1="15" x2="13" y2="15" />
  </svg>
);

const IconHeadset = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
    <rect x="2" y="12" width="4" height="6" rx="1" />
    <rect x="18" y="12" width="4" height="6" rx="1" />
    <path d="M13 21c0 1-1.5 2-3 2" />
  </svg>
);

const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const features = [
  { title: "Gestion des contrats", desc: "Souscrivez et pilotez vos contrats d’assurance en quelques clics.", icon: <IconDoc /> },
  { title: "Déclaration de sinistres", desc: "Déclarez vos sinistres rapidement avec photos et documents.", icon: <IconAlert /> },
  { title: "Suivi en temps réel", desc: "Suivez l’avancement de vos dossiers en direct.", icon: <IconClock /> },
  { title: "Paiements sécurisés", desc: "Réglez vos primes et retrouvez vos quittances sur mobile.", icon: <IconCard /> },
  { title: "Assistance client", desc: "Support direct et réactif depuis l’application.", icon: <IconHeadset /> },
  { title: "Sécurité maximale", desc: "Données protégées et connexions chiffrées.", icon: <IconShield /> },
];

const MobileAppDownloadPage = () => {
  const [release, setRelease] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | empty | error
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchLatest = async () => {
    setStatus("loading");
    setError("");
    try {
      const response = await fetch("/api/mobile-app/releases/latest");
      const contentType = response.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      const payload = isJson ? await response.json() : await response.text();
      if (!response.ok) {
        const message =
          typeof payload === "string" && payload.startsWith("<")
            ? "La dernière version n’est pas disponible (réponse non valide)."
            : typeof payload === "string"
              ? payload
              : payload?.message || payload?.error;
        throw new Error(message || "Impossible de récupérer la dernière version.");
      }
      const data = payload;
      if (releaseIsEmpty(data)) {
        setRelease(null);
        setStatus("empty");
        return;
      }
      setRelease(data);
      setStatus("ready");
    } catch (err) {
      setError(err?.message || "Une erreur est survenue.");
      setStatus("error");
    }
  };

  useEffect(() => {
    fetchLatest();
  }, []);

  const formattedSize = useMemo(() => formatFileSize(release?.fileSize), [release]);
  const formattedDate = useMemo(() => formatDate(release?.uploadedAt), [release]);
  const downloadUrl = downloadUrlForRelease(release);
  const releaseNotes = useMemo(
    () =>
      release?.releaseNotes
        ? release.releaseNotes
            .split(/\n|•/g)
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    [release]
  );

  const handleCopy = async () => {
    if (!release?.checksumSha256 || !navigator?.clipboard) return;
    await navigator.clipboard.writeText(release.checksumSha256);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const renderStatusBlock = () => {
    if (status === "loading") {
      return <div className="mobile-app-status">Chargement des informations...</div>;
    }
    if (status === "error") {
      return (
        <div className="mobile-app-status error">
          {error || "Impossible de charger la dernière version."}
          <button className="mobile-app-button" onClick={fetchLatest} style={{ marginLeft: 8 }}>
            Réessayer
          </button>
        </div>
      );
    }
    if (status === "empty") {
      return <div className="mobile-app-status">Aucune version disponible pour le moment.</div>;
    }
    return null;
  };

  return (
    <div className="mobile-app-page">
      <header className="mobile-app-nav">
        <div className="mobile-app-nav-content">
          <div className="mobile-app-brand">
            <img src={poolinsLogo} alt="Logo Poolins" />
            <div>
              <div className="title">Poolins</div>
              <div className="subtitle">Votre assurance, simplement et en toute sécurité.</div>
            </div>
          </div>
          <a className="mobile-app-link" href="#support">
            Nous contacter
          </a>
        </div>
      </header>

      <section className="mobile-app-hero">
        <div className="mobile-app-badge">Application officielle Poolins</div>
        <h1 className="mobile-app-title">Téléchargez l’application officielle Poolins</h1>
        <p className="mobile-app-subtitle">
          Gérez vos assurances, sinistres et paiements directement depuis votre téléphone.
        </p>
        <div className="mobile-app-cta-row">
          <a className="mobile-app-button" href={downloadUrl} download>
            ⬇ Télécharger l’application
          </a>
        </div>
        <div className="mobile-app-assurance">
          <span>Application officielle</span>
          <span>•</span>
          <span>Sécurisée</span>
          <span>•</span>
          <span>Gratuite</span>
        </div>
        <div style={{ marginTop: 10 }}>{renderStatusBlock()}</div>
      </section>

      <div className="mobile-app-section">
        <div className="mobile-app-card">
          <h3>Fonctionnalités clés</h3>
          <div className="mobile-app-features">
            {features.map((feature) => (
              <div className="mobile-app-feature" key={feature.title}>
                <div className="icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mobile-app-section">
        <div className="mobile-app-card">
          <h3>Détails de la version</h3>
          {status !== "ready" ? (
            renderStatusBlock()
          ) : (
            <>
              <div className="mobile-app-meta">
                <div className="meta-item">
                  <div className="meta-label">Version</div>
                  <div className="meta-value">{release?.versionName || "N/A"}</div>
                  <div className="mobile-app-tagline">Code {release?.versionCode || "-"}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Taille</div>
                  <div className="meta-value">{formattedSize}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Dernière mise à jour</div>
                  <div className="meta-value">{formattedDate}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Compatibilité</div>
                  <div className="meta-value">Android 8+</div>
                </div>
              </div>
              <div className="mobile-app-meta">
                <div className="meta-item">
                  <div className="meta-label">Checksum SHA-256</div>
                  <div className="checksum-row">
                    <code>{release?.checksumSha256 || "Non fourni"}</code>
                    {release?.checksumSha256 && (
                      <button className="copy-btn" onClick={handleCopy}>
                        {copied ? "Copié" : "Copier"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {releaseNotes.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="meta-label">Notes de version</div>
                  <div className="mobile-app-notes">
                    {releaseNotes.map((note, idx) => (
                      <div key={idx}>• {note}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mobile-app-section" id="support">
        <div className="mobile-app-card">
          <h3>Support & contact</h3>
          <div className="mobile-app-list">
            <li>
              <div className="icon">✔</div>
              <span>Application officielle de OSSAN ASUR</span>
            </li>
            <li>
              <div className="icon">🔒</div>
              <span>Données protégées et connexion sécurisée (HTTPS)</span>
            </li>
            <li>
              <div className="icon">📧</div>
              <span>
                Contact : <a href="mailto:ossanasur@ossanasur.com">ossanasur@ossanasur.com</a>
              </span>
            </li>
            <li>
              <div className="icon">📜</div>
              <a className="mobile-app-link" href="/privacy">
                Politique de confidentialité
              </a>
            </li>
          </div>
        </div>
      </div>

      <footer className="mobile-app-footer">
        OSSAN ASUR – ossanasur@ossanasur.com – © {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default MobileAppDownloadPage;
